import React, { useState, useCallback, useEffect, useRef } from 'react';
import './PokemonSearchBar.css';

function PokemonSearchBar({ onSearch, apiUrl }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const fetchSuggestions = useCallback(async (searchTerm) => {
    if (searchTerm.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/pokemon/autocomplete?q=${encodeURIComponent(searchTerm)}&size=8`
      );
      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Autocomplete error:', error);
      setSuggestions([]);
    }
  }, [apiUrl]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (query.trim()) {
        fetchSuggestions(query);
      } else {
        setSuggestions([]);
      }
    }, 150);

    return () => clearTimeout(debounceTimer);
  }, [query, fetchSuggestions]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    onSearch(suggestion);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        if (selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleBlur = (e) => {
    if (suggestionsRef.current && !suggestionsRef.current.contains(e.relatedTarget)) {
      setTimeout(() => setShowSuggestions(false), 150);
    }
  };

  return (
    <div className="pokemon-search-bar-container">
      <form onSubmit={handleSubmit} className="pokemon-search-form">
        <div className="pokemon-search-input-wrapper">
          <span className="pokemon-search-icon">⚔</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowSuggestions(true);
              setSelectedIndex(-1);
            }}
            onFocus={() => query.length >= 2 && setShowSuggestions(true)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder="Search Pokémon cards..."
            className="pokemon-search-input"
            autoComplete="off"
          />
          {query && (
            <button
              type="button"
              className="pokemon-clear-btn"
              onClick={() => {
                setQuery('');
                setSuggestions([]);
                inputRef.current?.focus();
              }}
            >
              ×
            </button>
          )}
        </div>
        <button type="submit" className="pokemon-search-btn">
          Search
        </button>
      </form>

      {showSuggestions && suggestions.length > 0 && (
        <ul className="pokemon-suggestions-list" ref={suggestionsRef}>
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              className={`pokemon-suggestion-item ${index === selectedIndex ? 'selected' : ''}`}
              onMouseDown={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="pokemon-suggestion-icon">⚔</span>
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PokemonSearchBar;
